<template>
  <div class="releases-page">
    <div class="releases-container">
      <!-- Header -->
      <div class="releases-header">
        <div>
          <p class="releases-kicker">Production deployments</p>
          <h1 class="releases-title">Release History</h1>
          <p class="releases-subtitle" v-if="store.currentSite">
            {{ store.currentSite.name }} · main-branch deployments
          </p>
        </div>
        <button
          @click="reload"
          :disabled="store.releaseHistoryLoading"
          data-testid="releases-refresh"
          class="rel-btn rel-btn-ghost"
        >
          Refresh
        </button>
      </div>

      <!-- AC010: provider failure error state. Rollback controls are disabled. -->
      <div
        v-if="store.releaseHistoryError"
        data-testid="releases-error-state"
        class="rel-alert rel-alert-danger"
      >
        <p class="rel-alert-title">
          Release history unavailable
        </p>
        <p class="rel-alert-body">
          {{ store.releaseHistoryError.message }}
        </p>
        <p class="rel-alert-code" data-testid="releases-error-code">
          ({{ store.releaseHistoryError.code }})
        </p>
        <button
          @click="reload"
          data-testid="releases-retry"
          class="rel-btn rel-btn-ghost rel-btn-danger-outline"
        >
          Retry
        </button>
      </div>

      <!-- Loading -->
      <div
        v-else-if="store.releaseHistoryLoading && !store.releaseHistory"
        data-testid="releases-loading"
        class="rel-empty"
      >
        Loading releases…
      </div>

      <!-- AC008: rollback job lifecycle banner (in-progress / success / failure) -->
      <div
        v-if="store.activeRollbackJob"
        :data-testid="`rollback-status-banner-${store.activeRollbackJob.status}`"
        class="rel-banner"
        :class="bannerClass(store.activeRollbackJob.status)"
      >
        <p class="rel-banner-title">
          Rollback {{ statusLabel(store.activeRollbackJob.status) }}
        </p>
        <p class="rel-banner-mono">
          {{ shortSha(store.activeRollbackJob.sourceCommitSha) }} →
          {{ shortSha(store.activeRollbackJob.targetCommitSha) }}
        </p>
        <p
          v-if="store.activeRollbackJob.status === 'verification_failed'"
          class="rel-banner-note"
          data-testid="rollback-verification-failed-detail"
        >
          Live commit
          {{ shortSha(store.activeRollbackJob.resolvedLiveCommitSha) }} does
          not match target.
        </p>
        <p
          v-if="store.activeRollbackJob.failureReason"
          class="rel-banner-note"
          data-testid="rollback-failure-reason"
        >
          {{ store.activeRollbackJob.failureReason }}
        </p>
      </div>

      <!-- Release rows -->
      <div
        v-if="store.releaseHistory && store.releaseHistory.releases.length"
        class="rel-list"
        data-testid="releases-list"
      >
        <div
          v-for="release in store.releaseHistory.releases"
          :key="release.sha"
          :data-testid="`release-row-${release.sha}`"
          class="rel-row"
        >
          <div class="rel-row-main">
            <div class="rel-row-line">
              <span class="rel-sha" data-testid="release-sha">
                {{ shortSha(release.sha) }}
              </span>
              <!-- AC002: exactly-one current-live marker -->
              <span
                v-if="release.isCurrentLive"
                data-testid="current-live-badge"
                class="rel-badge rel-badge-success"
              >
                Current live
              </span>
              <span
                v-else-if="release.deploymentState === 'verification_failed'"
                class="rel-badge rel-badge-warning"
              >
                Verification failed
              </span>
              <span
                v-else-if="release.deploymentState === 'failed'"
                class="rel-badge rel-badge-danger"
              >
                Rollback failed
              </span>
            </div>
            <p class="rel-row-msg">
              {{ firstLine(release.message) }}
            </p>
            <p class="rel-row-meta">
              {{ release.author }} · {{ formatTime(release.committedAt) }}
            </p>
          </div>
          <div class="rel-row-actions">
            <!-- AC005: only admins see the rollback button -->
            <button
              v-if="canRollback(release)"
              @click="openConfirm(release)"
              :disabled="rollbackDisabled"
              :data-testid="`rollback-button-${release.sha}`"
              class="rel-btn rel-btn-ghost rel-btn-sm"
            >
              Rollback
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="store.releaseHistory && !store.releaseHistory.releases.length"
        data-testid="releases-empty"
        class="rel-empty"
      >
        No deployable commits on the production branch yet.
      </div>

      <!-- AC002: live-commit-not-on-page hint -->
      <p
        v-if="store.releaseHistory && !store.releaseHistory.liveCommitInHistory && store.releaseHistory.liveCommitSha"
        data-testid="live-commit-offpage-hint"
        class="rel-hint"
      >
        Current live commit
        <span class="rel-mono">{{ shortSha(store.releaseHistory.liveCommitSha) }}</span>
        is not on this page.
      </p>

      <!-- AC004: explicit confirmation modal — shows target SHA + summary -->
      <div
        v-if="confirmTarget"
        data-testid="rollback-confirm-modal"
        class="rel-modal-backdrop"
      >
        <div class="rel-modal">
          <h2 class="rel-modal-title">Confirm rollback</h2>
          <p class="rel-modal-body">
            Production will be redeployed to the exact commit shown below.
          </p>
          <div class="rel-modal-card">
            <p>
              <span class="rel-modal-label">Target:</span>
              <span class="rel-mono rel-mono-strong" data-testid="confirm-target-sha">
                {{ confirmTarget.sha }}
              </span>
            </p>
            <p class="rel-modal-msg" data-testid="confirm-target-message">
              {{ firstLine(confirmTarget.message) }}
            </p>
            <p class="rel-modal-author">
              {{ confirmTarget.author }}
            </p>
          </div>
          <div v-if="confirmError" class="rel-modal-error" data-testid="rollback-confirm-error">
            {{ confirmError }}
          </div>
          <div class="rel-modal-actions">
            <button
              @click="cancelConfirm"
              data-testid="rollback-cancel"
              class="rel-btn rel-btn-ghost"
            >
              Cancel
            </button>
            <button
              @click="confirmRollback"
              :disabled="submitting"
              data-testid="rollback-confirm"
              class="rel-btn rel-btn-danger"
            >
              {{ submitting ? 'Submitting…' : 'Roll back' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSitesStore } from '../stores/sites'

const route = useRoute()
const store = useSitesStore()

const siteId = computed(() => route.params.id)
const confirmTarget = ref(null)
const confirmError = ref(null)
const submitting = ref(false)
let pollTimer = null

const reload = async () => {
  await store.fetchReleaseHistory(siteId.value)
}

onMounted(async () => {
  if (!store.currentSite || store.currentSite.id !== siteId.value) {
    await store.fetchSite(siteId.value)
  }
  await reload()
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const isAdmin = computed(() => !!store.user?.is_admin)

// AC010: when the provider is unavailable rollback eligibility is unknown, so
// all rollback controls must be disabled (no destructive action on partial state).
const rollbackDisabled = computed(() =>
  !!store.releaseHistoryError || store.releaseHistoryLoading
)

const canRollback = (release) => {
  if (!isAdmin.value) return false
  if (release.isCurrentLive) return false
  if (rollbackDisabled.value) return false
  return true
}

const openConfirm = (release) => {
  confirmError.value = null
  confirmTarget.value = release
}

// AC004: cancel must produce no side effects.
const cancelConfirm = () => {
  confirmTarget.value = null
  confirmError.value = null
}

const confirmRollback = async () => {
  if (!confirmTarget.value) return
  submitting.value = true
  confirmError.value = null
  try {
    const data = await store.requestRollback(siteId.value, confirmTarget.value.sha)
    confirmTarget.value = null
    // AC008: start polling for the job's lifecycle.
    if (data?.job?.id) {
      pollJob(data.job.id)
    }
  } catch (err) {
    const body = err.response?.data || {}
    confirmError.value = body.error || err.message || 'Rollback failed'
  } finally {
    submitting.value = false
  }
}

const pollJob = (jobId) => {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = setInterval(async () => {
    const job = await store.fetchReleaseJob(jobId)
    if (job && ['succeeded', 'failed', 'verification_failed'].includes(job.status)) {
      clearInterval(pollTimer)
      pollTimer = null
      await reload()
    }
  }, 3000)
}

watch(siteId, () => { reload() })

// ── presentation helpers ─────────────────────────────────────────────────
const shortSha = (sha) => (sha || '').slice(0, 7)
const firstLine = (msg) => (msg || '').split('\n')[0]
const formatTime = (ts) => {
  if (!ts) return ''
  try { return new Date(ts).toLocaleString() } catch { return ts }
}
const statusLabel = (s) => {
  switch (s) {
    case 'queued':              return 'queued'
    case 'in_progress':         return 'in progress'
    case 'succeeded':           return 'completed'
    case 'failed':              return 'failed'
    case 'verification_failed': return 'verification failed'
    default:                    return s || ''
  }
}
const bannerClass = (status) => {
  if (status === 'succeeded')           return 'rel-banner-success'
  if (status === 'failed')              return 'rel-banner-danger'
  if (status === 'verification_failed') return 'rel-banner-warning'
  return 'rel-banner-info'
}
</script>

<style scoped>
.releases-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 10% 0%, rgba(124,109,250,0.16), transparent 32rem),
    radial-gradient(circle at 90% 0%, rgba(56,189,248,0.08), transparent 30rem),
    var(--surface-base);
  padding: 32px 0 48px;
}
.releases-container {
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
}

.releases-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}
.releases-kicker {
  margin: 0 0 6px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.releases-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(26px, 3.6vw, 36px);
  font-weight: 700;
  letter-spacing: -0.03em;
}
.releases-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.rel-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  border: var(--glass-border);
  background: var(--glass-bg);
  color: var(--text-primary);
}
.rel-btn:hover:not(:disabled) {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}
.rel-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.rel-btn-sm { padding: 6px 10px; font-size: 12px; }
.rel-btn-ghost {
  background: var(--glass-bg);
  color: var(--text-secondary);
}
.rel-btn-ghost:hover:not(:disabled) {
  color: var(--text-primary);
}
.rel-btn-danger {
  background: var(--danger);
  border-color: rgba(239,68,68,0.4);
  color: #fff;
  box-shadow: 0 0 16px rgba(239,68,68,0.3);
}
.rel-btn-danger:hover:not(:disabled) {
  background: #ef4444;
  box-shadow: 0 0 22px rgba(239,68,68,0.4);
}
.rel-btn-danger-outline {
  margin-top: 12px;
  border-color: rgba(239,68,68,0.4);
  color: #fca5a5;
}

.rel-alert {
  padding: 16px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(239,68,68,0.3);
  background: rgba(239,68,68,0.10);
  margin-bottom: 20px;
}
.rel-alert-title { color: #fecaca; font-size: 13px; font-weight: 700; margin: 0; }
.rel-alert-body  { color: #fca5a5; font-size: 13px; margin: 4px 0 0; }
.rel-alert-code  { color: rgba(252,165,165,0.7); font-size: 11px; margin: 6px 0 0; font-family: ui-monospace, SFMono-Regular, monospace; }

.rel-empty {
  text-align: center;
  padding: 48px 16px;
  color: var(--text-muted);
  font-size: 13px;
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}

.rel-banner {
  padding: 16px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255,255,255,0.10);
  margin-bottom: 20px;
}
.rel-banner-title { font-size: 13px; font-weight: 700; margin: 0; }
.rel-banner-mono  { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; margin: 6px 0 0; opacity: 0.85; }
.rel-banner-note  { font-size: 12px; margin: 6px 0 0; opacity: 0.85; }
.rel-banner-info     { background: rgba(56,189,248,0.10);  border-color: rgba(56,189,248,0.3);  color: #bae6fd; }
.rel-banner-success  { background: rgba(34,197,94,0.10);   border-color: rgba(34,197,94,0.3);   color: #86efac; }
.rel-banner-warning  { background: rgba(245,158,11,0.10);  border-color: rgba(245,158,11,0.3);  color: #fde68a; }
.rel-banner-danger   { background: rgba(239,68,68,0.10);   border-color: rgba(239,68,68,0.3);   color: #fca5a5; }

.rel-list {
  border: var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  box-shadow: var(--elev-sm);
  overflow: hidden;
}
.rel-row {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.rel-row:last-child { border-bottom: 0; }
.rel-row:hover { background: var(--glass-bg-hover); }
.rel-row-main { flex: 1; min-width: 0; }
.rel-row-line { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.rel-sha {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  color: var(--text-muted);
}
.rel-row-msg  { margin: 6px 0 0; color: var(--text-primary); font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rel-row-meta { margin: 4px 0 0; color: var(--text-muted); font-size: 11px; }
.rel-row-actions { flex-shrink: 0; }

.rel-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
}
.rel-badge-success { background: rgba(34,197,94,0.14);  color: #86efac; border-color: rgba(34,197,94,0.3); }
.rel-badge-warning { background: rgba(245,158,11,0.14); color: #fde68a; border-color: rgba(245,158,11,0.3); }
.rel-badge-danger  { background: rgba(239,68,68,0.14);  color: #fca5a5; border-color: rgba(239,68,68,0.3); }

.rel-hint { color: var(--text-muted); font-size: 11px; margin-top: 12px; }
.rel-mono { font-family: ui-monospace, SFMono-Regular, monospace; }
.rel-mono-strong { color: var(--text-primary); margin-left: 6px; }

.rel-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 100;
}
.rel-modal {
  background: rgba(22,22,38,0.95);
  border: var(--glass-border-hi);
  border-radius: var(--radius-lg);
  box-shadow: var(--elev-lg);
  max-width: 480px;
  width: 100%;
  padding: 24px;
}
.rel-modal-title { margin: 0; color: var(--text-primary); font-size: 18px; font-weight: 700; }
.rel-modal-body  { margin: 8px 0 0; color: var(--text-secondary); font-size: 13px; }
.rel-modal-card {
  margin-top: 14px;
  padding: 14px;
  background: var(--glass-bg-active);
  border: var(--glass-border);
  border-radius: var(--radius-sm);
  font-size: 12px;
  color: var(--text-secondary);
}
.rel-modal-card p { margin: 0; }
.rel-modal-label  { color: var(--text-muted); }
.rel-modal-msg    { margin-top: 6px !important; color: var(--text-primary); }
.rel-modal-author { margin-top: 6px !important; color: var(--text-muted); }
.rel-modal-error  { margin-top: 12px; color: #fca5a5; font-size: 12px; }
.rel-modal-actions {
  margin-top: 22px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
